import React, { useState, useEffect, useCallback } from 'react';
import useXHR from '../../hooks/useXHR';
import { useAuth } from '../../contexts/AuthContext';

/**
 * DiscoverTrending - Shows what's trending across the InfoPilot community.
 * Displays popular search queries, trending categories, and community stats.
 * Refreshes every 5 minutes. Compact, addictive, and engaging.
 */
const DiscoverTrending = ({ onSearchQuery }) => {
  const { token } = useAuth();
  const xhr = useXHR(token);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadTrending = useCallback(() => {
    xhr.get('/trending', {
      onSuccess: (d) => setData(d)
    });
  }, [xhr]);

  useEffect(() => {
    loadTrending();
    const t = setInterval(loadTrending, 300000); // refresh every 5 min
    return () => clearInterval(t);
  }, [loadTrending]);

  if (!data) return null;

  const stats = data.community_stats || {};

  return (
    <div
      data-testid="discover-trending"
      style={{
        background: 'linear-gradient(135deg, rgba(252, 211, 77,0.08), rgba(253, 230, 138,0.06))',
        border: '1px solid rgba(252, 211, 77,0.15)',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 10,
        transition: 'all 0.3s'
      }}
    >
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid="trending-toggle"
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#a5b4fc'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-fire" style={{ color: '#f59e0b', fontSize: '0.85rem' }} />
          <span style={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.02em' }}>
            Discover What's Trending
          </span>
          <span style={{ color: '#666', fontSize: '0.55rem', fontWeight: 500 }}>
            {stats.results_this_week || 0} new URLs this week
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Quick stats pills */}
          <span style={{ background: 'rgba(252, 211, 77,0.15)', color: '#818cf8', padding: '2px 8px', borderRadius: 12, fontSize: '0.5rem', fontWeight: 600 }}>
            {stats.total_users || 0} explorers
          </span>
          <span style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', padding: '2px 8px', borderRadius: 12, fontSize: '0.5rem', fontWeight: 600 }}>
            {stats.total_results || 0} URLs collected
          </span>
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} style={{ color: '#666', fontSize: '0.6rem' }} />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 12px 10px' }}>
          {/* Trending Queries */}
          {data.trending_queries?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: '#888', fontSize: '0.5rem', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Trending Searches This Week
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {data.trending_queries.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => onSearchQuery && onSearchQuery(q.query)}
                    data-testid={`trending-query-${i}`}
                    style={{
                      background: i < 3 ? 'rgba(245,158,11,0.15)' : 'rgba(255, 255, 255, 0.85)',
                      border: `1px solid ${i < 3 ? 'rgba(245,158,11,0.25)' : 'rgba(203, 213, 225, 0.8)'}`,
                      color: i < 3 ? '#fbbf24' : '#a1a1aa',
                      padding: '3px 10px', borderRadius: 12, fontSize: '0.58rem', cursor: 'pointer',
                      fontWeight: i < 3 ? 700 : 500, transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', gap: 4
                    }}
                    title={`Search for "${q.query}" - searched ${q.count} times this week. Click to try it!`}
                  >
                    {i < 3 && <i className="fas fa-fire-alt" style={{ fontSize: '0.5rem' }} />}
                    {q.query.substring(0, 30)}{q.query.length > 30 ? '...' : ''}
                    <span style={{ opacity: 0.5, fontSize: '0.48rem' }}>{q.count}x</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Popular Categories */}
          {data.popular_categories?.length > 0 && (
            <div>
              <div style={{ color: '#888', fontSize: '0.5rem', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Most Popular Categories
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {data.popular_categories.map((cat, i) => (
                  <div
                    key={i}
                    style={{
                      background: 'rgba(253, 230, 138,0.08)',
                      border: '1px solid rgba(253, 230, 138,0.15)',
                      color: '#047857',
                      padding: '3px 10px', borderRadius: 12, fontSize: '0.55rem', fontWeight: 500
                    }}
                    title={`Protocol: ${cat.protocol || 'N/A'} - ${cat.count} URLs categorized`}
                  >
                    {cat.name.substring(0, 20)}{cat.name.length > 20 ? '..' : ''}
                    <span style={{ opacity: 0.5, marginLeft: 4, fontSize: '0.48rem' }}>{cat.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Microfont */}
          <div style={{ marginTop: 6, color: '#444', fontSize: '0.42rem', textAlign: 'center' }}>
            Click a trending search to try it yourself. Share your discoveries to help others learn!
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscoverTrending;
